import { useRouter } from "next/router";
import { useCallback, useEffect, useRef, useState } from "react";
import { useIntl } from "react-intl";
import { useSelector } from "react-redux";
import { useDraggable } from "react-use-draggable-scroll-safe";

import { AppState } from "shared/data/reducers";

import useSortFilterFromUrl from "../results/useSortFromQuery";
import type { SortTypes } from "../results/useSortFromQuery";

const useSorting = (isHotels: boolean) => {
  const ref = useRef<HTMLDivElement>(null);
  const { events } = useDraggable(ref, {
    applyRubberBandEffect: true,
    isMounted: !!ref.current,
  });

  const {
    changeSortFilter,
    sortOptions,
    currentSort,
    sortOption,
  } = useSortFilterFromUrl(isHotels);

  const [isDesktop, setIsDesktop] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      setIsDesktop(window.innerWidth >= 1010);
    };
    window.addEventListener("resize", handleResize);
    handleResize();
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  const router = useRouter();
  useEffect(() => {
    if (
      isHotels &&
      (!router.query.sort || router.query.sort === "") &&
      sortOptions.length > 0
    ) {
      changeSortFilter("MIN_PRICE_HOTEL");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isHotels, router.query.sort]);

  const currentSortIndex = sortOptions.findIndex(
    (it) => it.value === currentSort
  );

  const selectedStyle = useCallback(
    (index?: number) => {
      return currentSortIndex === index ? " selected" : "";
    },
    [currentSortIndex]
  );

  const onClickItem = useCallback(
    (index: number) => {
      changeSortFilter(sortOptions[index].value as SortTypes);
    },
    [changeSortFilter, sortOptions]
  );

  const { currency } = useSelector((state: AppState) => ({
    currency: state.search.currency,
  }));
  const customCurrency = router?.query.currency || currency;
  const intl = useIntl();

  return {
    ref,
    events,
    isDesktop,
    changeSortFilter,
    sortOptions,
    sortOption,
    currentSortIndex,
    selectedStyle,
    onClickItem,
    customCurrency,
    intl,
  };
};

export default useSorting;
